import { useSurvey } from "@hooks/useSurveySelectors";
import { getVisibleProperties } from "@shared/utils/propertyFiltering";
import type { PropertyFieldRule } from "@survey-types/survey.core.types";
import type { Property } from "@survey-types/survey.property.types";
import { useSearchParams } from "react-router";

/**
 * Custom hook for property comparison data processing
 * Encapsulates all Redux data fetching and business logic for property comparison
 *
 * @returns Processed data needed for property comparison rendering
 */
export const usePropertyComparisonData = () => {
  const survey = useSurvey();
  const [searchParams] = useSearchParams();

  const allProperties = survey?.properties || [];

  // Use shared property filtering utility
  let properties = getVisibleProperties(allProperties, survey);

  // Filter for shortlisted properties if query param is set
  const isClientShortList = searchParams.get("isClientShortList") === "true";
  if (isClientShortList) {
    properties = properties.filter((property) => property.isClientShortlist);
  }

  // Calculate property display orders based on visibility and filtering logic
  // Following the same approach as propertiesPreview: exclude hidden and current location properties from numbering
  const numberableProperties = properties.filter((property: Property) => {
    // Filter out current location properties from numbering
    if (property.isCurrentLocation) return false;

    return true;
  });

  // Create property orders map with zero-based index (matching propertiesPreview approach)
  const propertyOrders = numberableProperties.reduce(
    (acc: { [x: string]: number }, property: Property, index: number) => {
      return {
        ...acc,
        [property.propertyId]: index + 1,
      };
    },
    {} as { [x: string]: number },
  );

  const pdfPaperSize = survey?.pdfPaperSize;
  const propertyFieldRules =
    survey?.surveyPropertyFieldRule?.propertyFieldRuleList || [];

  // Filter and sort visible field rules
  const addressLineKeys = ["address1", "city", "stateProvince", "postalCode"];
  const areAddressFieldsPresent = propertyFieldRules.some(
    (field) =>
      addressLineKeys.includes(field.fieldKey || "") && !field.isHidden,
  );
  const visibleFields: PropertyFieldRule[] = propertyFieldRules
    .filter((field) => {
      if (areAddressFieldsPresent && field.fieldKey === "address1") {
        return true;
      }
      return !field.isHidden;
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Determine max properties based on orientation
  const maxProperties = pdfPaperSize === "Landscape" ? 7 : 4;
  const mode = (pdfPaperSize as "Landscape" | "Portrait") || "Landscape";

  return {
    properties,
    visibleFields,
    maxProperties,
    propertyOrders,
    mode,
  };
};
